import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  DollarSign, RefreshCw, Check, X, AlertTriangle, CheckCircle, Loader2, MessageSquare, Filter
} from "lucide-react";
import ResponsiveAdminLayout from '@/components/admin/ResponsiveAdminLayout';
import { ResponsiveMetricsGrid } from '@/components/ui/responsive-charts';
import { ResponsiveTable, ColumnDef } from '@/components/ui/responsive-table';
import { useTableColumns } from '@/hooks/useTableColumns';
import { ReembolsoService, Reembolso } from '@/services/reembolsoService';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ModalProcessarReembolsoPix from "@/components/ui/ModalProcessarReembolsoPix";
import ModalVisualizarReembolso from "@/components/ui/ModalVisualizarReembolso";
import { clientService, Client } from '@/services/clientService';

const AdminReembolsos = () => {
  const [reembolsos, setReembolsos] = useState<Reembolso[]>([]);
  const [stats, setStats] = useState<{ reembolsosPendentes: number; reembolsosProcessados: number; valorTotalPendente: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [observacaoModal, setObservacaoModal] = useState<{
    isOpen: boolean;
    reembolsoId: string;
    action: 'aprovar' | 'rejeitar' | 'processar';
    observacao: string;
  }>({
    isOpen: false,
    reembolsoId: '',
    action: 'aprovar',
    observacao: ''
  });
  const [modalPixReembolso, setModalPixReembolso] = useState<{
    isOpen: boolean;
    reembolso: Reembolso | null;
  }>({
    isOpen: false,
    reembolso: null
  });
  const [modalVisualizarReembolso, setModalVisualizarReembolso] = useState<{
    isOpen: boolean;
    reembolso: Reembolso | null;
  }>({
    isOpen: false,
    reembolso: null
  });
  const [userNames, setUserNames] = useState<{ [userId: string]: string }>({});

  // Carregar dados
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      

      const [reembolsosData, estatisticas] = await Promise.all([
        ReembolsoService.getAllReembolsos(1, 50, { status: statusFilter === 'ALL' ? undefined : statusFilter }),
        {} // Temporário - estatísticas não implementadas ainda
      ]);

      

      setReembolsos(reembolsosData.reembolsos);
      setStats({
        reembolsosPendentes: reembolsosData.reembolsos.filter(r => r.status === 'PENDENTE').length,
        reembolsosProcessados: reembolsosData.reembolsos.filter(r => r.status === 'PROCESSADO').length,
        valorTotalPendente: reembolsosData.reembolsos.filter(r => r.status === 'PENDENTE').reduce((sum, r) => sum + r.valor, 0)
      });
    } catch (error) {
      
      setError('Erro ao carregar dados dos reembolsos');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  // Carregar dados quando o filtro mudar
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Buscar nomes dos usuários dos reembolsos
  useEffect(() => {
    const fetchUserNames = async () => {
      const uniqueUserIds = Array.from(new Set(reembolsos.map(r => r.user_id)));
      const missingIds = uniqueUserIds.filter(id => !userNames[id]);
      if (missingIds.length === 0) return;
      const updates: { [userId: string]: string } = {};
      await Promise.all(missingIds.map(async (id) => {
        try {
          const client = await clientService.getClientById(id);
          updates[id] = client.nome_completo;
        } catch {
          updates[id] = '(Nome não encontrado)';
        }
      }));
      setUserNames(prev => ({ ...prev, ...updates }));
    };
    if (reembolsos.length > 0) fetchUserNames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reembolsos]);

  // Configuração das colunas da tabela
  const { createColumn } = useTableColumns<Reembolso>();
  
  const columns: ColumnDef<Reembolso>[] = [
    createColumn('user', 'Cliente', (reembolso) => (
      <div>
        <p className="font-medium">{userNames[reembolso.user_id] || 'Carregando...'}</p>
        <p className="text-sm text-gray-500">ID: {reembolso.user_id}</p>
      </div>
    )),
    createColumn('valor', 'Valor', (reembolso) => (
      <span className="font-semibold text-green-600">
        {ReembolsoService.formatarValor(reembolso.valor)}
      </span>
    )),
    createColumn('motivo', 'Motivo', (reembolso) => 
      reembolso.motivo || 'Não informado'
    ),
    createColumn('status', 'Status', (reembolso) => (
      <Badge className={getStatusColor(reembolso.status)}>
        {getStatusLabel(reembolso.status)}
      </Badge>
    )),
    createColumn('created_at', 'Data', (reembolso) => 
      new Date(reembolso.created_at).toLocaleDateString('pt-BR')
    ),
    createColumn('actions', 'Ações', (reembolso) => (
      <div className="flex gap-2">
        {reembolso.status === 'PENDENTE' && (
          <>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => handleProcessarComPix(reembolso)}
              title="Aprovar e Processar PIX"
            >
              <Check className="w-4 h-4" />
            </Button>
            <Button 
              size="sm" 
              variant="destructive"
              onClick={() => openObservacaoModal(reembolso.id, 'rejeitar')}
            >
              <X className="w-4 h-4" />
            </Button>
          </>
        )}
        {reembolso.status === 'PROCESSADO' && (
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => handleVerDetalhes(reembolso)}
            title="Ver Detalhes do Reembolso"
          >
            <DollarSign className="w-4 h-4" />
          </Button>
        )}
      </div>
    ))
  ];

  // Card mobile personalizado para reembolsos
  const renderReembolsoMobileCard = (reembolso: Reembolso, index: number) => (
    <Card key={index} className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-lg">{userNames[reembolso.user_id] || 'Carregando...'}</h3>
        <Badge className={getStatusColor(reembolso.status)}>
          {getStatusLabel(reembolso.status)}
        </Badge>
      </div>
      
      <div className="space-y-2 text-sm mb-4">
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Valor:</span>
          <span className="font-semibold text-green-600">
            {ReembolsoService.formatarValor(reembolso.valor)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Data:</span>
          <span>{new Date(reembolso.created_at).toLocaleDateString('pt-BR')}</span>
        </div>
        {reembolso.motivo && (
          <div>
            <span className="text-gray-500">Motivo:</span>
            <p className="text-sm mt-1">{reembolso.motivo}</p>
          </div>
        )}
      </div>
      
      <div className="flex gap-2">
        {reembolso.status === 'PENDENTE' && (
          <>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => handleProcessarComPix(reembolso)}
              className="flex-1"
            >
              <Check className="w-4 h-4 mr-2" />
              Aprovar PIX
            </Button>
            <Button 
              size="sm" 
              variant="destructive"
              onClick={() => openObservacaoModal(reembolso.id, 'rejeitar')}
            >
              <X className="w-4 h-4" />
            </Button>
          </>
        )}
        {reembolso.status === 'PROCESSADO' && (
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => handleVerDetalhes(reembolso)}
            className="w-full"
          >
            <DollarSign className="w-4 h-4 mr-2" />
            Ver Detalhes
          </Button>
        )}
      </div>
    </Card>
  );

  // Funções auxiliares para status
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDENTE': return 'bg-yellow-100 text-yellow-800';
      case 'APROVADO': return 'bg-blue-100 text-blue-800';
      case 'PROCESSADO': return 'bg-green-100 text-green-800';
      case 'REJEITADO': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDENTE': return 'Pendente';
      case 'APROVADO': return 'Aprovado';
      case 'PROCESSADO': return 'Processado';
      case 'REJEITADO': return 'Rejeitado';
      default: return status;
    }
  };

  // Métricas baseadas nos dados reais
  const metrics = stats ? [
    {
      title: "Pendentes",
      value: stats.reembolsosPendentes,
      change: "aguardando processamento",
      bg: "bg-gradient-to-r from-orange-500 to-red-500",
      icon: AlertTriangle
    },
    {
      title: "Processados",
      value: stats.reembolsosProcessados,
      change: "reembolsos concluídos",
      bg: "bg-gradient-to-r from-green-500 to-emerald-500",
      icon: CheckCircle
    },
    {
      title: "Valor Pendente",
      value: ReembolsoService.formatarValor(stats.valorTotalPendente),
      change: "a ser processado",
      bg: "bg-gradient-to-r from-blue-500 to-cyan-500",
      icon: DollarSign
    }
  ] : [];

  const handleAcaoReembolso = async () => {
    try {
      const { reembolsoId, action, observacao } = observacaoModal;

      

      let updatedReembolso: Reembolso;

      switch (action) {
        case 'aprovar':
          updatedReembolso = await ReembolsoService.atualizarStatus(reembolsoId, 'APROVADO', observacao);
          break;
        case 'rejeitar':
          updatedReembolso = await ReembolsoService.atualizarStatus(reembolsoId, 'REJEITADO', observacao);
          break;
        case 'processar':
          updatedReembolso = await ReembolsoService.atualizarStatus(reembolsoId, 'PROCESSADO', observacao);
          break;
        default:
          throw new Error('Ação inválida');
      }

      

      // Atualizar lista local
      setReembolsos(prev => 
        prev.map(r => r.id === reembolsoId ? updatedReembolso : r)
      );

      // Fechar modal
      setObservacaoModal({
        isOpen: false,
        reembolsoId: '',
        action: 'aprovar',
        observacao: ''
      });

      // Recarregar estatísticas
      loadData();

    } catch (error) {
      
      setError('Erro ao processar reembolso');
    }
  };

  const openObservacaoModal = (reembolsoId: string, action: 'aprovar' | 'rejeitar' | 'processar') => {
    setObservacaoModal({
      isOpen: true,
      reembolsoId,
      action,
      observacao: ''
    });
  };

  const handleProcessarComPix = (reembolso: Reembolso) => {
    setModalPixReembolso({
      isOpen: true,
      reembolso
    });
  };

  const handlePixSuccess = () => {
    
    setModalPixReembolso({
      isOpen: false,
      reembolso: null
    });
    loadData(); // Recarregar dados
  };

  const handleVerDetalhes = (reembolso: Reembolso) => {
    setModalVisualizarReembolso({
      isOpen: true,
      reembolso: reembolso
    });
  };

  if (loading) {
    return (
      <ResponsiveAdminLayout
        title="Gestão de Reembolsos"
        subtitle="Processar solicitações de reembolso de créditos"
      >
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin" />
          <span className="ml-2 text-gray-600">Carregando reembolsos...</span>
        </div>
      </ResponsiveAdminLayout>
    );
  }

  if (error) {
    return (
      <ResponsiveAdminLayout
        title="Gestão de Reembolsos"
        subtitle="Processar solicitações de reembolso de créditos"
      >
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={loadData}>
                Tentar novamente
              </Button>
            </div>
          </CardContent>
        </Card>
      </ResponsiveAdminLayout>
    );
  }

  return (
    <ResponsiveAdminLayout
      title="Gestão de Reembolsos"
      subtitle="Processar solicitações de reembolso de créditos"
    >
      {/* Filtros */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filtrar por status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos os status</SelectItem>
                <SelectItem value="PENDENTE">Pendente</SelectItem>
                <SelectItem value="APROVADO">Aprovado</SelectItem>
                <SelectItem value="REJEITADO">Rejeitado</SelectItem>
                <SelectItem value="PROCESSADO">Processado</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Reembolsos Table */}
      <ResponsiveTable
        data={reembolsos}
        columns={columns}
        renderMobileCard={renderReembolsoMobileCard}
        loading={loading}
        emptyMessage="Nenhum reembolso encontrado"
      />

      {/* Modal para Observações */}
      <Dialog open={observacaoModal.isOpen} onOpenChange={(open) => 
        setObservacaoModal(prev => ({ ...prev, isOpen: open }))
      }>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {observacaoModal.action === 'aprovar' && 'Aprovar Reembolso'}
              {observacaoModal.action === 'rejeitar' && 'Rejeitar Reembolso'}
              {observacaoModal.action === 'processar' && 'Processar Reembolso'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">
                Observação {observacaoModal.action === 'rejeitar' && '(obrigatória)'}:
              </label>
              <Textarea
                value={observacaoModal.observacao}
                onChange={(e) => setObservacaoModal(prev => ({ 
                  ...prev, 
                  observacao: e.target.value 
                }))}
                placeholder="Digite uma observação..."
                className="mt-1"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button 
                variant="outline" 
                onClick={() => setObservacaoModal(prev => ({ ...prev, isOpen: false }))}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleAcaoReembolso}
                disabled={observacaoModal.action === 'rejeitar' && !observacaoModal.observacao}
                className={
                  observacaoModal.action === 'aprovar' ? 'bg-blue-600 hover:bg-blue-700' :
                  observacaoModal.action === 'processar' ? 'bg-green-600 hover:bg-green-700' :
                  'bg-red-600 hover:bg-red-700'
                }
              >
                {observacaoModal.action === 'aprovar' && 'Aprovar'}
                {observacaoModal.action === 'rejeitar' && 'Rejeitar'}
                {observacaoModal.action === 'processar' && 'Processar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 🆕 Modal para Processar Reembolso PIX */}
      <ModalProcessarReembolsoPix
        isOpen={modalPixReembolso.isOpen}
        onClose={() => setModalPixReembolso({ isOpen: false, reembolso: null })}
        reembolso={modalPixReembolso.reembolso}
        onSuccess={handlePixSuccess}
        userName={modalPixReembolso.reembolso ? userNames[modalPixReembolso.reembolso.user_id] : undefined}
      />

      {/* 🆕 Modal para Visualizar Detalhes do Reembolso */}
      <ModalVisualizarReembolso
        isOpen={modalVisualizarReembolso.isOpen}
        onClose={() => setModalVisualizarReembolso({ isOpen: false, reembolso: null })}
        reembolso={modalVisualizarReembolso.reembolso}
      />
    </ResponsiveAdminLayout>
  );
};

export default AdminReembolsos; 